import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon, faBars, faTimes } from "@fortawesome/free-solid-svg-icons";

export default function Header() {
  const [dark, setDark] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");

    if (savedTheme) {
      setDark(savedTheme === "dark");
    } else {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches;
      setDark(systemTheme);
    }
  }, []);

  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [dark]);

  const navLinkStyle = ({ isActive }) =>
    `relative transition duration-300 w-fit
    ${isActive
      ? "text-primary dark:text-primary"
      : "text-textMain dark:text-textDark"
    }
    after:content-[''] after:absolute after:left-0 after:-bottom-1
    after:h-[2px] after:w-0 after:bg-current
    after:transition-all after:duration-300
    hover:after:w-full
    ${isActive ? "after:w-full" : ""}
    `;

  return (
    <>
      <header className="sticky top-0 z-50 bg-appBg border-b border-borderLight dark:bg-appDark dark:border-borderDark">
        <div className="mx-auto px-6 py-4 flex items-center justify-between">

          {/* LOGO */}
          <NavLink to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl overflow-hidden">
              <img
                src="/online-clipboard.png"
                alt="Online Clipboard Logo"
                className="h-full w-full object-cover"
              />
            </div>

            <div>
              <h1 className="font-semibold text-sm sm:text-base md:text-lg text-titleLight dark:text-titleDark leading-tight">
                Online-Clipboard
              </h1>

              <p className="text-[10px] sm:text-xs text-subText dark:text-subTextDark">
                Online Clipboard – Quick Text Transfer
              </p>
            </div>
          </NavLink>

          {/* NAV + TOGGLE */}
          <div className="flex items-center gap-6">

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
              <NavLink to="/" end className={navLinkStyle}>Home</NavLink>
              <NavLink to="/about" className={navLinkStyle}>About</NavLink>
              <NavLink to="/faq" className={navLinkStyle}>FAQ</NavLink>
              <NavLink to="/Blog" className={navLinkStyle}>Blog</NavLink>
              <NavLink to="/contact" className={navLinkStyle}>Contact</NavLink>

              <NavLink to="/privacy-policy" className={navLinkStyle}>Privacy Policy</NavLink>
              <NavLink to="/terms" className={navLinkStyle}>Terms</NavLink>
              <NavLink to="/disclaimer" className={navLinkStyle}>Disclaimer</NavLink>
            </nav>

            {/* Dark Mode */}
            <button
              onClick={() => setDark(!dark)}
              className="h-10 w-10 rounded-full bg-input text-textMain hover:bg-borderLight
              dark:bg-inputDark dark:text-textDark dark:hover:bg-borderDark dark:text-white dark:hover:bg-borderDark
              transition-all duration-200 flex items-center justify-center"
            >
              <FontAwesomeIcon icon={dark ? faSun : faMoon} />
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMenuOpen(true)}
              className="md:hidden h-10 w-10 rounded-full bg-input text-textMain hover:bg-borderLight
dark:bg-inputDark dark:text-textDark dark:hover:bg-borderDark flex items-center justify-center transition"
            >
              <FontAwesomeIcon icon={faBars} />
            </button>

          </div>
        </div>
      </header >

      {/* Overlay */}
      {
        menuOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setMenuOpen(false)}
          />
        )
      }

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-boxLight dark:bg-boxDark
  shadow-lg transform transition-transform duration-300 z-50
  ${menuOpen ? "translate-x-0" : "translate-x-full"}`}
      >

        {/* Drawer Header (Logo + Close Same Row) */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-borderDark">

          <NavLink
            to="/"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3"
          >
            <div className="h-9 w-9 rounded-xl overflow-hidden bg-boxLight dark:bg-transparent p-1">
              <img
                src="/online-clipboard.png"
                alt="Logo"
                className="h-full w-full object-contain"
              />
            </div>

            <div>
              <h2 className="font-semibold text-base text-titleLight dark:text-titleDark leading-tight">
                Online-Clipboard
              </h2>
              <p className="text-xs text-subText dark:text-subTextDark">
                Free Online Clipboard
              </p>
            </div>
          </NavLink>

          <button onClick={() => setMenuOpen(false)}>
            <FontAwesomeIcon
              icon={faTimes}
              className="text-xl text-textMain hover:text-titleLight dark:text-textDark dark:hover:text-titleDark transition"
            />
          </button>
        </div>

        {/* Drawer Links */}
        <div className="flex flex-col gap-6 px-6 mt-6 text-lg font-medium">

          <NavLink
            to="/"
            end
            onClick={() => setMenuOpen(false)}
            className={navLinkStyle}
          >
            Home
          </NavLink>

          <NavLink
            to="/about"
            onClick={() => setMenuOpen(false)}
            className={navLinkStyle}
          >
            About
          </NavLink>

          <NavLink
            to="/faq"
            onClick={() => setMenuOpen(false)}
            className={navLinkStyle}
          >
            FAQ
          </NavLink>

          <NavLink
            to="/Blog"
            onClick={() => setMenuOpen(false)}
            className={navLinkStyle}
          >
            Blog
          </NavLink>

          <NavLink
            to="/contact"
            onClick={() => setMenuOpen(false)}
            className={navLinkStyle}
          >
            Contact
          </NavLink>

          <NavLink
            to="/privacy-policy"
            onClick={() => setMenuOpen(false)}
            className={navLinkStyle}
          >
            Privacy Policy
          </NavLink>

          <NavLink
            to="/terms"
            onClick={() => setMenuOpen(false)}
            className={navLinkStyle}
          >
            Terms
          </NavLink>

          <NavLink
            to="/disclaimer"
            onClick={() => setMenuOpen(false)}
            className={navLinkStyle}
          >
            Disclaimer
          </NavLink>

        </div>
      </div>
    </>
  );
}